/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, fromEvent, merge, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { User } from '../../../../models/User';
import { HelpCenterService } from '../../../../services/help-center.service';
import { UserService } from '../../../../services/user.service';

interface buttonState {
	text: string;
	active: boolean;
	students?: User[];
}

class FileSizeError extends Error {}

@Component({
	selector: 'app-import-student-list',
	templateUrl: './import-student-list.component.html',
	styleUrls: ['./import-student-list.component.scss'],
})
export class ImportStudentListComponent implements OnInit, OnDestroy {
	// no static: true as it is a part of a template
	// that may not be in the DOm
	@ViewChild('dropArea') dropArea: ElementRef;
	@ViewChild('file') fileRef: ElementRef;

	@ViewChild('upload', { read: TemplateRef, static: true }) uploadTpl: TemplateRef<HTMLElement>;
	@ViewChild('spinning', { read: TemplateRef, static: true }) spinningTpl: TemplateRef<HTMLElement>;
	@ViewChild('issues', { read: TemplateRef, static: true }) issuesTpl: TemplateRef<HTMLElement>;
	tpl: TemplateRef<HTMLElement>;
	tplImplicit: Record<string, any> = {};

	destroy$ = new Subject();
	constructor(private userService: UserService, private dialogRef: MatDialogRef<ImportStudentListComponent>, private helpCenter: HelpCenterService) {}

	buttonStateSubject$: BehaviorSubject<buttonState> = new BehaviorSubject<buttonState>({ text: 'Add students', active: false });
	buttonState$: Observable<buttonState>;

	draggedAFile$ = new Subject<File>();

	ngOnInit(): void {
		this.buttonState$ = this.buttonStateSubject$.asObservable();
		// render template to the DOM
		this.tpl = this.uploadTpl;
		this.tplImplicit = { text: 'Choose a CSV file', secondaryText: 'or drag it here', showClose: false };
		// need this to access this.fileRef
		this.attachObservables();
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	filename = '';

	attachObservables() {
		this.buttonStateSubject$.next({ text: 'Add students', active: false });
		setTimeout(() => {
			// reset bvalue to upload same file
			fromEvent(this.fileRef.nativeElement, 'click')
				.pipe(
					tap((evt: Event) => {
						(evt.target as HTMLInputElement).value = '';
					}),
					takeUntil(this.destroy$)
				)
				.subscribe();

			const choseAFile$ = fromEvent(this.fileRef.nativeElement, 'change').pipe(map(() => this.fileRef.nativeElement.files[0] as File));

			merge(choseAFile$, this.draggedAFile$)
				.pipe(
					tap(() => {
						this.tplImplicit = { hint: 'Examining file' };
						this.tpl = this.spinningTpl;
					}),
					switchMap((f) => {
						const fr = new FileReader();
						fr.readAsBinaryString(f);
						this.filename = f.name;
						const size = (f.size / 1024 / 1024).toFixed(2);
						if (+size > 5) {
							throw new FileSizeError('file size is over 5 MB');
						}
						return fromEvent(fr, 'load');
					}),
					map((evt: ProgressEvent) => {
						this.tplImplicit = { hint: 'Trying to read students list' };
						// TODO check is right type of file?
						// create a workbook
						const workbook = XLSX.read((evt.target as FileReader).result, { type: 'binary' });
						const sheetName = workbook.SheetNames[0];
						const sheet = workbook.Sheets[sheetName];
						const data = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, blankrows: false });
						const indexOfEmail: number = data[0].findIndex((v) => v.toLowerCase().indexOf('email') !== -1);
						return data.map((r) => r[indexOfEmail]);
					}),
					tap(() => {
						this.tplImplicit = { hint: 'Loading...' };
						this.buttonStateSubject$.next({ text: `Add students`, active: false });
					}),
					map((mm: string[]) => {
						return (
							mm
								// retain only strings that looks like an email
								.filter((m) => m.indexOf('@') !== -1)
								// unique values
								.filter((v, i, me) => me.indexOf(v) === i)
								// posible white space
								.map((v) => v.trim())
						);
					}),
					switchMap((emailsUnverified: string[]) => {
						const list$ = this.userService.listOf({ email: emailsUnverified });
						return list$.pipe(
							map((uu: User[]) => {
								return [emailsUnverified, uu];
							})
						);
					}),
					tap(([emailsUnverified, usersVerified]) => {
						const emailsVerified = (usersVerified as User[]).map((u: User) => u.primary_email);
						const emailsFailed = (emailsUnverified as string[]).filter((e: string) => !emailsVerified.includes(e));
						// we have a failure
						if (emailsFailed.length > 0) {
							this.tpl = this.issuesTpl;
							const s = emailsFailed.length > 1 ? 's' : '';
							const warning = `${emailsFailed.length}/${emailsUnverified.length} email${s} have error${s}`;
							this.tplImplicit = { fails: emailsFailed, warning };
							if (emailsVerified.length > 0) {
								this.buttonStateSubject$.next({ students: <User[]>usersVerified, text: `Add ${emailsVerified.length} students`, active: true });
							} else {
								this.buttonStateSubject$.next({ text: `Add students`, active: false });
							}
							// don’t have what to show
						} else if (emailsVerified.length === 0) {
							this.tpl = this.issuesTpl;
							this.tplImplicit = { fails: [], warning: 'No students found in spreadsheet' };
							this.buttonStateSubject$.next({ text: `Add students`, active: false });
						} else {
							// we are OK
							this.tpl = this.uploadTpl;
							this.attachObservables();

							this.tplImplicit = { text: this.filename, showClose: true };
							this.buttonStateSubject$.next({ students: <User[]>usersVerified, text: `Add ${emailsVerified.length} students`, active: true });
							//this.dialogRef.close(usersVerified);
						}
					}),
					takeUntil(this.destroy$),
					catchError((err) => {
						this.tpl = this.issuesTpl;
						const warning = 'The file provided is not usable';
						this.tplImplicit = { fails: [], warning };
						this.buttonStateSubject$.next({ text: `Add students`, active: false });
						if (err instanceof HttpErrorResponse) {
							throw err;
						}
						if (err instanceof FileSizeError) {
							this.tplImplicit.warning = 'File size is too big';
						}
						return of(null);
					})
				)
				.subscribe();
		}, 0);
	}

	onFileDrag(fileList: FileList) {
		console.log('dropped inner!');
		const file = fileList[0];
		this.draggedAFile$.next(file);
	}

	addStudents(ss: User[]) {
		if (ss.length > 0) {
			this.dialogRef.close(ss);
		}
	}

	goUpload() {
		this.tpl = this.uploadTpl;
		this.tplImplicit = { text: 'Choose a CSV file', secondaryText: 'or drag it here', showClose: false };
		// reattach lost bindings
		this.attachObservables();
	}

	getHelp() {
		this.helpCenter.openHelp();
	}

	closeModal() {
		this.dialogRef.close();
	}

	protected readonly console = console;
}
